import type {
  ExhibitionArtistData,
  PublicExhibitionData,
  VenueDetailData,
} from '../dto/EntityRepository';

export interface InterestExhibitionCardData {
  key: string;
  venueId: string;
  venueCode: string;
  venueName: string;
  venueNameEn: string;
  venueInstagramUrl: string;
  venueNaverMapUrl: string;
  exhibition: PublicExhibitionData;
}

const normalizeArtist = (artist: ExhibitionArtistData): ExhibitionArtistData => ({
  ...artist,
  artistId: artist.artistId != null && Number(artist.artistId) > 0
    ? Number(artist.artistId)
    : null,
  name: artist.name ?? '',
  nameEn: artist.nameEn ?? '',
  instagramHandle: artist.instagramHandle ?? '',
  architraveHandle: artist.architraveHandle ?? '',
});

const normalizeExhibition = (
  exhibition: PublicExhibitionData,
): PublicExhibitionData => ({
  ...exhibition,
  title: exhibition.title ?? '',
  titleEn: exhibition.titleEn ?? '',
  startDate: exhibition.startDate ?? '',
  endDate: exhibition.endDate ?? '',
  officialUrl: exhibition.officialUrl ?? null,
  artists: (exhibition.artists ?? []).map(normalizeArtist),
});

export const buildInterestExhibitionCardKey = (
  venueId: string,
  exhibitionId: number,
): string => `${encodeURIComponent(venueId)}:${encodeURIComponent(String(exhibitionId))}`;

export const buildInterestExhibitionCards = (
  venue: VenueDetailData | null | undefined,
): InterestExhibitionCardData[] => {
  if (!venue) return [];

  const venueId = String(venue.id ?? '');
  const venueCode = venue.venueCode ?? '';
  const venueName = venue.name ?? '';
  const venueNameEn = venue.nameEn ?? '';
  const venueInstagramUrl = venue.instagramUrl?.trim() ?? '';
  const venueNaverMapUrl = venue.naverMapUrl?.trim() ?? '';

  return (venue.exhibitions ?? []).map((rawExhibition) => {
    const exhibition = normalizeExhibition(rawExhibition);
    return {
      key: buildInterestExhibitionCardKey(venueId, exhibition.id),
      venueId,
      venueCode,
      venueName,
      venueNameEn,
      venueInstagramUrl,
      venueNaverMapUrl,
      exhibition,
    };
  });
};

export const mergeInterestExhibitionCards = (
  current: InterestExhibitionCardData[],
  incoming: InterestExhibitionCardData[],
): InterestExhibitionCardData[] => {
  const seen = new Set<string>();

  return [...current, ...incoming].filter((card) => {
    if (seen.has(card.key)) return false;
    seen.add(card.key);
    return true;
  });
};

export const removeInterestExhibitionCard = (
  cards: InterestExhibitionCardData[],
  key: string,
): InterestExhibitionCardData[] => cards.filter((card) => card.key !== key);

export interface InterestVenueGroupData {
  venueId: string;
  venueCode: string;
  venueName: string;
  venueNameEn: string;
  venueInstagramUrl: string;
  venueNaverMapUrl: string;
  exhibitions: InterestExhibitionCardData[];
}

export const groupInterestExhibitionsByVenue = (
  cards: InterestExhibitionCardData[],
): InterestVenueGroupData[] => {
  const groups: InterestVenueGroupData[] = [];
  const indexByVenueId = new Map<string, number>();

  cards.forEach((card) => {
    const existingIndex = indexByVenueId.get(card.venueId);
    if (existingIndex == null) {
      indexByVenueId.set(card.venueId, groups.length);
      groups.push({
        venueId: card.venueId,
        venueCode: card.venueCode,
        venueName: card.venueName,
        venueNameEn: card.venueNameEn,
        venueInstagramUrl: card.venueInstagramUrl,
        venueNaverMapUrl: card.venueNaverMapUrl,
        exhibitions: [card],
      });
      return;
    }

    groups[existingIndex].exhibitions.push(card);
  });

  return groups;
};

export const removeInterestExhibitionCardsByVenue = (
  cards: InterestExhibitionCardData[],
  venueId: string,
): InterestExhibitionCardData[] => cards.filter((card) => card.venueId !== venueId);

export const getInterestedVenueIds = (
  cards: InterestExhibitionCardData[],
): string[] => {
  const seen = new Set<string>();

  return cards.reduce<string[]>((venueIds, card) => {
    if (!card.venueId || seen.has(card.venueId)) return venueIds;
    seen.add(card.venueId);
    venueIds.push(card.venueId);
    return venueIds;
  }, []);
};

const normalizeMmcaExhibitionUrl = (value: string): string => {
  try {
    const url = new URL(value);
    const isMmcaExhibitionDetail = url.hostname.replace(/^www\./, '') === 'mmca.go.kr'
      && url.pathname === '/exhibitions/exhibitionsDetail.do'
      && url.searchParams.has('exhId');

    if (!isMmcaExhibitionDetail || url.searchParams.get('exhFlag') === '1') {
      return value;
    }

    const params = new URLSearchParams(url.search);
    params.delete('exhFlag');
    url.search = `exhFlag=1&${params.toString()}`;
    return url.href;
  } catch {
    return value;
  }
};

export const getInterestExhibitionTargetUrl = (
  officialUrl: string | null,
  venueInstagramUrl: string,
): string => {
  const trimmedOfficialUrl = officialUrl?.trim();
  if (!trimmedOfficialUrl) return venueInstagramUrl;
  return normalizeMmcaExhibitionUrl(trimmedOfficialUrl);
};

export const formatInterestExhibitionEndDate = (value: string): string => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value ? `~${value}` : '';
  return `~${match[1].slice(-2)}.${match[2]}.${match[3]}.`;
};

export interface InterestExhibitionEndDateDisplay {
  text: string;
  dDayText: string;
  isEndingSoon: boolean;
}

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const SEOUL_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const parseDate = (value: string): [number, number, number] | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return null;

  return [year, month, day];
};

export const getInterestExhibitionEndDateDisplay = (
  value: string,
  now = new Date(),
): InterestExhibitionEndDateDisplay => {
  const text = formatInterestExhibitionEndDate(value);
  const endDate = parseDate(value);
  if (!endDate) return { text, dDayText: '', isEndingSoon: false };

  const todayParts = SEOUL_DATE_FORMATTER.formatToParts(now);
  const getTodayPart = (type: Intl.DateTimeFormatPartTypes): number => (
    Number(todayParts.find((part) => part.type === type)?.value)
  );
  const today = Date.UTC(
    getTodayPart('year'),
    getTodayPart('month') - 1,
    getTodayPart('day'),
  );
  const end = Date.UTC(endDate[0], endDate[1] - 1, endDate[2]);
  const daysRemaining = (end - today) / DAY_IN_MILLISECONDS;
  const isEndingSoon = daysRemaining >= 0 && daysRemaining <= 7;

  return {
    text,
    dDayText: isEndingSoon ? `(D-${daysRemaining})` : '',
    isEndingSoon,
  };
};
